import { Injectable, Signal, computed, inject, signal, untracked } from '@angular/core';
import { Store } from '@ngrx/store';

import {
  FilterMethod,
  MapFilterType
} from '@core/models/data-visualization/data-visualization.state';
import { EntityRef } from '@core/services/gbe-error-parser/warning.model';
import { setActionFocus } from '@core/store/data-visualization/data-visualization.actions';
import {
  selectAggregatorFilter,
  selectMapVisualization,
  selectTimelineVisualization
} from '@core/store/data-visualization/data-visualization.selectors';
import { selectTimelinePreferences } from '@core/store/user-preferences/user-preferences.selectors';

import {
  ActionFocusState,
  FocusIndex,
  HoverTarget,
  ResolvedFocus,
  RouteFocusState,
  buildFocusIndex,
  sameHoverTarget
} from './visual-focus.model';

const EMPTY_SET: ReadonlySet<string> = new Set();

/**
 * Resolves what the map and the timeline should be showing as focused.
 *
 * Hover lives here and only here: it fires at pointer rate, and `setFilterOnAggregator` is in
 * the popup replicator's `syncActions`, so routing it through the store would put a
 * BroadcastChannel message on the wire for every frame the pointer is moving. A pin is a
 * click, so it can afford the store — and needs it, to reach a detached popup.
 */
@Injectable({ providedIn: 'root' })
export class VisualFocusService {
  private readonly store = inject(Store);

  private readonly hovered = signal<HoverTarget | null>(null, { equal: sameHoverTarget });

  private readonly mapVisu = this.store.selectSignal(selectMapVisualization);
  private readonly timelineVisu = this.store.selectSignal(selectTimelineVisualization);
  private readonly aggregatorFilter = this.store.selectSignal(selectAggregatorFilter);
  private readonly timelinePreferences = this.store.selectSignal(selectTimelinePreferences);

  /** Depends on store state alone, so a hover never rebuilds it. */
  private readonly index = computed<FocusIndex>(() =>
    buildFocusIndex(this.mapVisu().markers, this.timelineVisu().rows, this.mapVisu().routes)
  );

  /**
   * Every route the map holds, and every action either panel knows about. The map painter walks
   * these to give each feature its tier, so they name the whole world a focus is resolved
   * against — not just the part of it the focus lights.
   */
  readonly knownRouteUids = computed<ReadonlySet<string>>(
    () => new Set(this.mapVisu().routes.map((route) => route.uid))
  );

  readonly knownActionUids = computed<ReadonlySet<string>>(
    () => new Set(this.index().routeOfAction.keys())
  );

  /**
   * The route a single-route aggregator filter names. A map route click dispatches exactly
   * that, so a pinned route needs no channel of its own.
   */
  readonly pinnedRouteUid = computed<string | null>(() => {
    const filter = this.aggregatorFilter();
    if (filter.filterMethod !== FilterMethod.BY_AGGREGATOR) return null;

    const ids = filter.filteredAggregatorIds;
    return ids.length === 1 && ids[0] !== MapFilterType.RESET ? ids[0] : null;
  });

  /**
   * The actions a job pin names. Exposed on its own because it survives a hover, which the
   * resolved focus below does not: the timeline's accent ring marks what is *pinned*, and
   * reading it off `focus` would make the ring blink out the moment the pointer moves.
   */
  readonly pinnedJobActionUids = computed<ReadonlySet<string>>(() => {
    const seed = this.aggregatorFilter().focusedActionUid;
    if (!seed) return EMPTY_SET;
    return new Set([seed, ...this.jobSiblingsOf(seed)]);
  });

  /** What a click has selected, with nothing hovered. */
  private readonly pinnedFocus = computed<ResolvedFocus | null>(() => {
    if (this.aggregatorFilter().focusedActionUid) return this.resolveJob();

    const pinnedRoute = this.pinnedRouteUid();
    return pinnedRoute === null ? null : this.resolveRoute(pinnedRoute);
  });

  /**
   * A hover refines the pin rather than replacing it: the pin keeps its routes lit as the
   * context you are reading in, and the hovered action becomes the one thing at full strength,
   * which is what links a timeline block to its marker. The pin comes back untouched when the
   * pointer leaves — nothing about it is written by a hover.
   *
   * A hover that falls *outside* a pinned route is ignored outright, for the reason given at
   * the guard below.
   *
   * The computed's memoization also gives the map painter a free identity check: an unchanged
   * focus is the same object, so it can skip the repaint.
   *
   * With the "focus on hover" preference off, the hover is still recorded — the map's hit
   * layers rely on `coversRoute` / `coversAction` to arbitrate between themselves — but takes
   * no part in the focus, so only a click changes what is dimmed.
   */
  readonly focus: Signal<ResolvedFocus | null> = computed(() => {
    const pinned = this.pinnedFocus();
    const hovered = this.timelinePreferences().focusOnHover ? this.hovered() : null;
    if (!hovered) return pinned;

    // A clicked *route* holds the view against everything outside it. It has narrowed the map
    // to itself through the hard filter, so a hover elsewhere cannot put that other route's
    // stops on screen — the layer drawing them is filtered to the pinned route — while it does
    // push the pinned route's own stops to `dim`. The route stayed lit and every one of its
    // stops vanished, numbers still drawn over nothing. A hover *inside* the pin still refines
    // it, which is what links a timeline block to its marker.
    if (pinned?.kind === 'route' && !coversHover(pinned, hovered)) return pinned;

    const live =
      hovered.kind === 'route'
        ? this.resolveRoute(hovered.routeUid)
        : this.resolveAction(hovered.actionUid);
    if (pinned === null) return live;

    return {
      ...live,
      litRouteUids: new Set([...pinned.litRouteUids, ...live.litRouteUids]),
      // The stricter of the two wins: a route pin has already declared every other line
      // noise, and lifting them back to context on a hover would flicker the whole network
      // each time the pointer crosses a stop.
      otherRoutes: pinned.otherRoutes === 'dim' ? 'dim' : live.otherRoutes
    };
  });

  /**
   * The routes the map is in a position to draw: the selection, narrowed by a hard aggregator
   * filter when one is active. A filter takes its routes off the layers outright, so a focus
   * naming one of the others is naming something no longer on screen.
   */
  private readonly drawableRouteUids = computed<ReadonlySet<string>>(() => {
    const known = this.knownRouteUids();

    const filter = this.aggregatorFilter();
    if (filter.filterMethod !== FilterMethod.BY_AGGREGATOR) return known;

    const ids = filter.filteredAggregatorIds.filter((id) => id !== MapFilterType.RESET);
    if (ids.length === 0) return known;

    return new Set(ids.filter((id) => known.has(id)));
  });

  /**
   * The routes a focus lights *and* the map can draw, empty when it lights none of them.
   *
   * The two sets come apart more easily than it looks. A job pin survives the aggregator
   * filter by design — the reducer keeps `focusedActionUid` across `setFilterOnAggregator` —
   * so pinning a job on one route and then filtering to another leaves the focus lighting a
   * line the filter has already taken away. And a marker is not always a stop on a route: a
   * transport order carries its own uid as its aggregator, so hovering one lights a "route"
   * this map has never heard of.
   *
   * Both cases must leave the network alone rather than dim it to nothing, which is what makes
   * this its own signal instead of a `litRouteUids` read.
   */
  readonly litDrawableRouteUids = computed<ReadonlySet<string>>(() => {
    const focus = this.focus();
    if (focus === null) return EMPTY_SET;

    const drawable = this.drawableRouteUids();
    return new Set([...focus.litRouteUids].filter((uid) => drawable.has(uid)));
  });

  /**
   * The routes actually drawn on the map right now, which is what decides whether the stop
   * numbers are shown: they are legible only while a single route is on display, and ambiguous
   * the moment a second one is — two stops numbered `3` sitting side by side say nothing.
   *
   * A focus narrows this to the routes it lights, but only to the ones the map still draws: a
   * focus that lights nothing drawable has narrowed nothing, and the numbers belong to
   * whatever the filter left on screen.
   */
  readonly displayedRouteUids = computed<ReadonlySet<string>>(() => {
    const drawable = this.drawableRouteUids();
    const lit = this.litDrawableRouteUids();

    return lit.size > 0 ? lit : drawable;
  });

  /**
   * The stops whose number the map should be drawing: those of the one route on display, and
   * none at all as soon as a second route is on it.
   */
  readonly numberedActionUids = computed<ReadonlySet<string>>(() => {
    const displayed = this.displayedRouteUids();
    if (displayed.size !== 1) return EMPTY_SET;

    const [routeUid] = displayed;
    return new Set(this.index().actionsOfRoute.get(routeUid) ?? []);
  });

  /**
   * The routes a finding's reference to a stop or a job stands for, among the routes the map and
   * the timeline hold: a reference the panels have never indexed resolves to none.
   */
  routesOfRef(ref: EntityRef): readonly string[] {
    if (ref.uid === null) return [];
    switch (ref.entityType) {
      case 'ROUTE_ACTION': {
        const routeUid = this.index().routeOfAction.get(ref.uid);
        return routeUid ? [routeUid] : [];
      }
      case 'JOB':
        return [...this.routesOf(this.index().actionsOfJob.get(ref.uid) ?? [])];
      default:
        return [];
    }
  }

  hoverRoute(routeUid: string | null): void {
    this.hovered.set(routeUid === null ? null : { kind: 'route', routeUid });
  }

  hoverAction(actionUid: string | null): void {
    this.hovered.set(actionUid === null ? null : { kind: 'action', actionUid });
  }

  /**
   * Reading this must not subscribe: the map's hover handlers call it outside the zone.
   */
  currentHoverTarget(): HoverTarget | null {
    return untracked(() => this.hovered());
  }

  /**
   * The entity a line belongs to, or the line's own uid when the line *is* the entity.
   *
   * What the hard filter needs to reach a job's operations: the click that raises it carries
   * the uid of the *line* it landed on, and a job's markers name the job rather than the arc.
   * Read from a store subscription, so it must not subscribe.
   */
  aggregatorOfRoute(routeUid: string): string {
    return untracked(() => this.index().aggregatorOfRoute.get(routeUid) ?? routeUid);
  }

  /**
   * Drops a hover whose owner can no longer take it back. This service is root-provided while
   * the panels that raise a hover are not, and neither a component torn down under the pointer
   * nor a map layer switched to `visibility: none` under it emits the matching leave event. The
   * hover would otherwise outlive its panel and be painted onto the next map that attaches.
   *
   * `kind` restricts the clear to hovers the caller owns, so the map's two layers do not
   * cancel each other. Read outside the zone, so this must not subscribe.
   */
  clearHover(kind?: HoverTarget['kind']): void {
    const hovered = untracked(() => this.hovered());
    if (hovered === null) return;
    if (kind !== undefined && hovered.kind !== kind) return;
    this.hovered.set(null);
  }

  /**
   * True when the live hover already covers this route — it either IS this route, or is
   * one of that route's own actions, which is the more specific target and outranks the
   * line. A stop sitting on its own route makes both hit layers fire on every frame the
   * pointer holds there; without this, the route handler would re-affirm itself once per
   * frame instead of deferring to the more specific hover the marker already set. Read
   * outside the zone, so this must not subscribe.
   */
  coversRoute(routeUid: string): boolean {
    return untracked(() => {
      const hovered = this.hovered();
      if (!hovered) return false;
      if (hovered.kind === 'route') return hovered.routeUid === routeUid;

      const index = this.index();
      const ofAction = index.routeOfAction.get(hovered.actionUid);
      // An action names the entity, a job's arc names itself, so the two are compared through
      // the entity the arc belongs to. Without that step the test never matches for a job and
      // the line goes on re-affirming its own hover over the marker's, once per frame.
      return (
        ofAction === routeUid ||
        ofAction === (index.aggregatorOfRoute.get(routeUid) ?? routeUid)
      );
    });
  }

  /** True when the live hover is exactly this action. Read outside the zone. */
  coversAction(actionUid: string): boolean {
    return untracked(() => {
      const hovered = this.hovered();
      return hovered?.kind === 'action' && hovered.actionUid === actionUid;
    });
  }

  /**
   * Pins the jobs handled at `actionUid`, or clears the pin if they are already pinned.
   *
   * Dispatches its own action rather than folding into `setFilterOnAggregator`: that action
   * replaces the whole aggregator filter, and a map click on a stop dispatches it for the same
   * click that raises a pin — the stop's hit layer overlaps its route's. The pin therefore
   * needs a writer that touches nothing else.
   */
  toggleJobPin(actionUid: string): void {
    const alreadyPinned = this.aggregatorFilter().focusedActionUid === actionUid;
    this.store.dispatch(setActionFocus({ actionUid: alreadyPinned ? null : actionUid }));
  }

  /**
   * Drops a raised job pin, and does nothing at all when none is raised.
   *
   * The guard is not an optimisation: the reducer rebuilds `filterOnAggregator` for every
   * dispatch, and a new object there re-runs the map's whole hard filter and repaints every
   * focus target. A background click that changes nothing must not cost that.
   */
  clearJobPin(): void {
    if (!this.aggregatorFilter().focusedActionUid) return;
    this.store.dispatch(setActionFocus({ actionUid: null }));
  }

  routeState(routeUid: string): RouteFocusState {
    const focus = this.focus();
    if (!focus) return 'none';
    if (focus.litRouteUids.has(routeUid)) return 'lit';
    return focus.otherRoutes === 'soft' ? 'soft' : 'dim';
  }

  actionState(actionUid: string): ActionFocusState {
    const focus = this.focus();
    if (!focus) return 'none';
    if (focus.litActionUids.has(actionUid)) return 'lit';
    if (focus.softActionUids.has(actionUid)) return 'soft';
    return 'dim';
  }

  private resolveRoute(routeUid: string): ResolvedFocus {
    return {
      kind: 'route',
      litRouteUids: new Set([routeUid]),
      litActionUids: new Set(this.index().actionsOfRoute.get(routeUid) ?? []),
      softActionUids: new Set(),
      otherRoutes: 'dim'
    };
  }

  private resolveAction(actionUid: string): ResolvedFocus {
    const softActionUids = this.jobSiblingsOf(actionUid);
    const litActionUids = new Set([actionUid]);
    return {
      kind: 'action',
      litRouteUids: this.routesOf([actionUid, ...softActionUids]),
      litActionUids,
      softActionUids,
      otherRoutes: 'soft'
    };
  }

  private resolveJob(): ResolvedFocus {
    const litActionUids = this.pinnedJobActionUids();
    return {
      kind: 'job',
      litRouteUids: this.routesOf(litActionUids),
      litActionUids,
      softActionUids: new Set(),
      otherRoutes: 'soft'
    };
  }

  /**
   * Every other action sharing a job with `seedActionUid`. The walk is over jobs, never
   * over routes: a multi-echelon order's legs sit on different routes and all of them
   * count as related.
   */
  private jobSiblingsOf(seedActionUid: string): ReadonlySet<string> {
    const index = this.index();
    const siblings = new Set<string>();
    for (const job of index.jobsOfAction.get(seedActionUid) ?? []) {
      for (const actionUid of index.actionsOfJob.get(job) ?? []) {
        if (actionUid !== seedActionUid) siblings.add(actionUid);
      }
    }
    return siblings;
  }

  private routesOf(actionUids: Iterable<string>): ReadonlySet<string> {
    const index = this.index();
    const routeUids = new Set<string>();
    for (const actionUid of actionUids) {
      const routeUid = index.routeOfAction.get(actionUid);
      if (routeUid) routeUids.add(routeUid);
    }
    return routeUids;
  }
}

/** Whether a focus already lights what the pointer is over: the line itself, or one of its own actions. */
function coversHover(focus: ResolvedFocus, hovered: HoverTarget): boolean {
  return hovered.kind === 'route'
    ? focus.litRouteUids.has(hovered.routeUid)
    : focus.litActionUids.has(hovered.actionUid);
}
